import React, { useEffect, useState, useContext } from 'react';
import { graphql } from 'gatsby';

//@ts-ignore
import * as styles from './tile-carousel-container.module.css';
import RichText from '../Global/RichText';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    KontentRadioMultiChoice,
    KontentNumberValue,
    KontentStringValue,
    CTAConfiguration,
    KontentIcon,
    KontentMedia,
    BrandColor,
} from '../../../types/KontentProps';
import {
    getIcon,
    getMultiChoice,
    getMediaData,
    getColor,
} from '../Global/DataUtils/index';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { getFluidImageData } from '../Global/DataUtils/gatsbyImageUtils';
import GatsbyImage from 'gatsby-image';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { defaultDomainCountryCodes } from '../../global/countries';

type LayoutType = 'container' | 'carousel';
type HideDescription = 'no' | 'yes';

interface Tile extends CTAConfiguration {
    anchor_name?: KontentStringValue;
    description_copy: KontentStringValue;
    display_title: KontentStringValue;
    hide_description_on_mobile: KontentRadioMultiChoice<HideDescription>;
    icon: KontentIcon;
    image: KontentMedia;
    tile_color: BrandColor;
    tile_font_color: BrandColor;
}

interface KontentTile {
    value: {
        elements: Tile;
    }[];
}
interface Props {
    component_anchor_name: KontentStringValue;
    small_display_number: KontentNumberValue;
    small_layout_display_type: KontentRadioMultiChoice<LayoutType>;
    large_display_number: KontentNumberValue;
    large_layout_display_type: KontentRadioMultiChoice<LayoutType>;
    referenceName: KontentStringValue;
    tiles: KontentTile;
}

const TileCarouselContainer: React.FC<Props> = ({
    component_anchor_name,
    small_display_number = { value: 6 },
    small_layout_display_type,
    large_display_number = { value: 12 },
    large_layout_display_type,
    referenceName = '',
    tiles,
    ...props
}) => {
    const orange = '#f06623';
    const smallLayoutDisplayType = getMultiChoice(small_layout_display_type);
    const largeLayoutDisplayType = getMultiChoice(large_layout_display_type);

    const breakpoints = useBreakpoint();
    const currentDisplayNumber = breakpoints.md
        ? large_display_number?.value
        : small_display_number?.value;
    const [visibleTiles, setVisibleTiles] = useState(
        tiles?.value?.slice(0, currentDisplayNumber)
    );
    const [currentPage, setCurrentPage] = useState(1);
    const isSmallTiles =
        tiles?.value[0]?.elements?.description_copy?.value === '<p><br></p>';

    useEffect(() => {
        setVisibleTiles(tiles?.value?.slice(0, currentDisplayNumber));
    }, [currentDisplayNumber]);

    const handleViewMoreClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const newIdx = currentPage + 1;
        setVisibleTiles(tiles?.value?.slice(0, currentDisplayNumber * newIdx));
        setCurrentPage(newIdx);
    };

    const isCarousel =
        (!breakpoints.md && smallLayoutDisplayType === 'carousel') ||
        (breakpoints.md && largeLayoutDisplayType === 'carousel');

    const showViewMoreButton = () => {
        if (isCarousel) {
            return;
        }

        if (tiles?.value?.length > visibleTiles?.length) {
            return (
                <div className={styles.buttonContainer}>
                    <button type="button" onClick={handleViewMoreClick}>
                        VIEW MORE
                    </button>
                </div>
            );
        }
    };

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newPage =
            (currentPage - 1) %
            Math.ceil(tiles?.value?.length / currentDisplayNumber);
        if (newPage < 0) {
            newPage =
                Math.ceil(tiles?.value?.length / currentDisplayNumber) - 1;
        }
        let newIdx = currentPage * currentDisplayNumber;
        let currentTile = newIdx + currentDisplayNumber;

        setVisibleTiles(tiles?.value?.slice(newIdx, currentTile));
        setCurrentPage(newPage);
    };

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newPage =
            (currentPage + 1) %
            Math.ceil(tiles?.value?.length / currentDisplayNumber);
        let newIdx = currentPage * currentDisplayNumber;
        let currentTile = newIdx + currentDisplayNumber;

        setVisibleTiles(tiles?.value?.slice(newIdx, currentTile));
        setCurrentPage(newPage);
    };

    return (
        <section className={styles.section} id={component_anchor_name?.value}>
            <div className={isCarousel ? styles.carousel : styles.container}>
                <div
                    className={
                        styles.tiles +
                        (isSmallTiles ? ' ' + styles.smallTiles : '')
                    }
                >
                    {visibleTiles?.map((tile, idx) => {
                        const icon = getIcon(tile?.elements?.icon);
                        const image = getFluidImageData(tile?.elements?.image);
                        let linkHref = BuildLinkHref(
                            tile.elements?.cta_configuration__cta_location,
                            tile.elements
                                ?.cta_configuration__cta_location_anchor_point
                        );
                        const pageMetadata = useContext(PageMetadataContext);
                        const preferredLanguage =
                            pageMetadata.preferredLanguage || 'default';
                        linkHref = linkHref
                            ? defaultDomainCountryCodes.indexOf(
                                  preferredLanguage.toLowerCase()
                              ) > -1
                                ? linkHref
                                : `/${preferredLanguage.toLowerCase()}${linkHref}`
                            : '';
                        return (
                            <Tile
                                href={linkHref}
                                key={idx}
                                id={
                                    tile?.elements?.anchor_name?.value ||
                                    undefined
                                }
                                style={{
                                    backgroundColor: getColor(
                                        tile.elements?.tile_color
                                    ) as any,
                                    color: getColor(
                                        tile.elements?.tile_font_color
                                    ) as any,
                                }}
                                className={
                                    (isSmallTiles
                                        ? styles.smallTile
                                        : styles.tile) +
                                    ' ' +
                                    (icon?.url && ' ' + styles.iconTile)
                                }
                            >
                                {!isSmallTiles && (
                                    <div className={styles.tileWrapper}>
                                        {icon?.url && (
                                            <div className={styles.tileIcon}>
                                                {' '}
                                                <img
                                                    src={icon.url}
                                                    alt={icon.description}
                                                />{' '}
                                            </div>
                                        )}
                                        {image?.source && (
                                            <div className={styles.tileIcon}>
                                                <GatsbyImage
                                                    fluid={image.source}
                                                    alt={image.alt}
                                                    fadeIn={false}
                                                    style={{
                                                        overflow: 'visible',
                                                        minWidth: '100px',
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.tileContent}>
                                            <h4>
                                                {
                                                    tile.elements.display_title
                                                        ?.value
                                                }
                                            </h4>
                                            {tile.elements
                                                ?.description_copy && (
                                                <div
                                                    className={
                                                        getMultiChoice(
                                                            tile.elements
                                                                .hide_description_on_mobile
                                                        ) === 'yes'
                                                            ? styles.hideDescriptionMobile
                                                            : ''
                                                    }
                                                >
                                                    {RichText(
                                                        tile.elements
                                                            ?.description_copy
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {isSmallTiles && (
                                    <div
                                        className={styles.tileWrapNoDescription}
                                    >
                                        {icon?.url && (
                                            <div className={styles.tileIcon}>
                                                <img
                                                    src={icon.url}
                                                    alt={icon.description}
                                                />
                                            </div>
                                        )}
                                        {image?.source && (
                                            <GatsbyImage
                                                fluid={image.source}
                                                alt={image.alt}
                                                style={{
                                                    overflow: 'visible',
                                                }}
                                                fadeIn={false}
                                            />
                                        )}
                                        <div className={styles.tileContent}>
                                            <h4>
                                                {
                                                    tile.elements.display_title
                                                        ?.value
                                                }
                                            </h4>
                                        </div>
                                    </div>
                                )}
                            </Tile>
                        );
                    })}
                </div>
                {isCarousel && (
                    <div className={styles.controls}>
                        <ArrowLgButton
                            className={styles.prev}
                            strokeColor={orange}
                            direction="left"
                            handleClick={handleClickPrev}
                        />
                        <ArrowLgButton
                            className={styles.next}
                            strokeColor={orange}
                            direction="right"
                            handleClick={handleClickNext}
                        />
                    </div>
                )}
            </div>
            {showViewMoreButton()}
        </section>
    );
};

export default TileCarouselContainer;

interface TileProps {
    href: string;
    style: React.CSSProperties;
    className: string;
    id: string | undefined;
}
const Tile: React.FC<TileProps> = (props) => {
    let hrefAnchorLink = props.href;

    if (hrefAnchorLink === '') {
        return (
            <div style={props.style} className={props.className} id={props.id}>
                {props?.children}
            </div>
        );
    }

    if (
        hrefAnchorLink.includes('https://') &&
        !hrefAnchorLink.startsWith('http')
    ) {
        hrefAnchorLink = 'http' + hrefAnchorLink.split('http').at(-1);
    }

    return (
        <a
            href={hrefAnchorLink}
            style={props.style}
            className={props.className}
            id={props.id}
        >
            {props?.children}
        </a>
    );
};

export const fragmentTileCarouselContainer = graphql`
    fragment TileCarouselContainer on kontent_item_component___tile_carousel__container_ {
        id
        elements {
            component_anchor_name {
                value
            }
            large_display_number {
                value
            }
            large_layout_display_type {
                value {
                    codename
                }
            }
            small_display_number {
                value
            }
            small_layout_display_type {
                value {
                    codename
                }
            }
            tiles {
                value {
                    ... on kontent_item_content_item___tile {
                        elements {
                            anchor_name {
                                value
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            description_copy {
                                ...kontentRichText
                            }
                            display_title {
                                value
                            }
                            hide_description_on_mobile {
                                value {
                                    codename
                                }
                            }
                            icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            image {
                                ...kontentImage
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    fluid(
                                                        maxWidth: 338
                                                        maxHeight: 186
                                                    ) {
                                                        ...KontentAssetFluid
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            tile_color {
                                ...kontentColor
                            }
                            tile_font_color {
                                ...kontentColor
                            }
                        }
                    }
                }
            }
        }
    }
`;
